import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Trash2, ArrowUpCircle, Loader2, AlertTriangle, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const PAGE_SIZE = 50;

const formatCurrency = (v) => !v
  ? '-'
  : new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);

const formatDate = (v) => {
  if (!v) return '-';
  try { return new Date(v).toLocaleDateString('es-ES'); } catch { return v; }
};

const PapeleraPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [restoring, setRestoring] = useState(false);
  const [emptying, setEmptying] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('estado', 'papelera');
      params.append('skip', String(page * PAGE_SIZE));
      params.append('limit', String(PAGE_SIZE));
      params.append('sort_by', 'updated_at');
      params.append('sort_order', 'desc');
      const r = await axios.get(`${API}/assets?${params.toString()}`, { withCredentials: true });
      const data = r.data?.items || r.data || [];
      setItems(data);
      setTotal(r.data?.total ?? data.length);
    } catch {
      toast.error('Error al cargar la papelera');
    } finally { setLoading(false); }
  }, [page]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const allPageIds = items.map(a => a.id);
  const allPageSelected = allPageIds.length > 0 && allPageIds.every(id => selectedIds.includes(id));
  const togglePageSelection = () => {
    if (allPageSelected) setSelectedIds(prev => prev.filter(id => !allPageIds.includes(id)));
    else setSelectedIds(prev => Array.from(new Set([...prev, ...allPageIds])));
  };
  const toggleOne = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const handleRestore = async () => {
    if (!selectedIds.length) return;
    if (!window.confirm(`Restaurar ${selectedIds.length} activos a estado "nuevo"?`)) return;
    setRestoring(true);
    try {
      const r = await axios.post(`${API}/assets/bulk-restore`, { ids: selectedIds }, { withCredentials: true });
      toast.success(`${r.data.restored} activos restaurados`);
      setSelectedIds([]);
      fetchItems();
    } catch (err) {
      toast.error('Error: ' + (err.response?.data?.detail || err.message));
    } finally { setRestoring(false); }
  };

  const handleEmptyPapelera = async () => {
    if (!window.confirm(`Eliminar DEFINITIVAMENTE todos los activos de la papelera (${total})? Esta accion no se puede deshacer.`)) return;
    setEmptying(true);
    try {
      const r = await axios.post(`${API}/assets/empty-papelera`, {}, { withCredentials: true });
      toast.success(`${r.data.deleted} activos eliminados de forma definitiva`);
      setSelectedIds([]);
      fetchItems();
    } catch (err) {
      toast.error('Error: ' + (err.response?.data?.detail || err.message));
    } finally { setEmptying(false); }
  };

  // Purge a single asset — admin only. Confirmation required; cannot be undone.
  const [purgingId, setPurgingId] = useState(null);
  const handlePurgeOne = async (a) => {
    const label = a.codigo_npl || a.referencia_catastral || a.id.slice(0, 8);
    if (!window.confirm(`Eliminar DEFINITIVAMENTE el activo ${label}? Esta acción no se puede deshacer.`)) return;
    setPurgingId(a.id);
    try {
      await axios.delete(`${API}/assets/${a.id}/purge`, { withCredentials: true });
      toast.success(`Activo ${label} eliminado definitivamente`);
      setSelectedIds(prev => prev.filter(x => x !== a.id));
      fetchItems();
    } catch (err) {
      toast.error('Error: ' + (err.response?.data?.detail || err.message));
    } finally { setPurgingId(null); }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="papelera-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="heading-1 flex items-center gap-3"><Trash2 className="w-7 h-7 text-slate-500" /> Papelera</h1>
          <p className="body-text mt-1">
            {total} activos. Los activos en papelera se ocultan de la base principal y pueden restaurarse o eliminarse definitivamente.
          </p>
        </div>
        {isAdmin && total > 0 && (
          <button
            onClick={handleEmptyPapelera}
            disabled={emptying}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 text-sm font-medium"
            data-testid="empty-papelera-btn"
          >
            {emptying ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertTriangle className="w-4 h-4" />}
            {emptying ? 'Vaciando...' : 'Vaciar papelera'}
          </button>
        )}
      </div>

      {/* Bulk bar */}
      {selectedIds.length > 0 && (
        <div
          className="flex flex-wrap items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg shadow-md"
          style={{ position: 'sticky', top: 8, zIndex: 30 }}
          data-testid="papelera-bulk-bar"
        >
          <span className="text-sm font-medium text-slate-700">{selectedIds.length} seleccionados</span>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={handleRestore} disabled={restoring} className="btn-primary text-sm flex items-center gap-1" data-testid="restore-selected-btn">
              {restoring ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowUpCircle className="w-3.5 h-3.5" />}
              {restoring ? 'Restaurando...' : 'Restaurar'}
            </button>
            <button onClick={() => setSelectedIds([])} className="text-slate-500 text-sm hover:text-slate-700">Cancelar</button>
          </div>
        </div>
      )}

      {loading && <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 text-blue-600 animate-spin" /></div>}

      {!loading && items.length === 0 && (
        <div className="card-base p-12 text-center" data-testid="papelera-empty">
          <Trash2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="heading-3 mb-2">La papelera esta vacia</h3>
          <p className="body-text">Los activos movidos a papelera aparecen aqui para restaurar o eliminar.</p>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="table-wrapper" style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 260px)', minHeight: '400px' }}>
          <table style={{ minWidth: '900px', borderCollapse: 'collapse', width: '100%' }} data-testid="papelera-table">
            <thead style={{ position: 'sticky', top: 0, zIndex: 5, background: '#f8fafc' }}>
              <tr>
                <th className="table-header text-center" style={{ width: '32px', background: '#f8fafc' }}>
                  <input type="checkbox" checked={allPageSelected} onChange={togglePageSelection} className="w-4 h-4 rounded border-slate-300" data-testid="papelera-select-all" />
                </th>
                <th className="table-header" style={{ width: '80px', background: '#f8fafc' }}>NPL</th>
                <th className="table-header" style={{ width: '110px', background: '#f8fafc' }}>Referencia</th>
                <th className="table-header" style={{ width: '160px', background: '#f8fafc' }}>Direccion</th>
                <th className="table-header" style={{ width: '100px', background: '#f8fafc' }}>Municipio</th>
                <th className="table-header" style={{ width: '90px', background: '#f8fafc' }}>Tipo Op.</th>
                <th className="table-header" style={{ width: '90px', background: '#f8fafc' }}>Precio</th>
                <th className="table-header" style={{ width: '110px', background: '#f8fafc' }}>Descartado</th>
                <th className="table-header" style={{ width: '120px', background: '#f8fafc' }}>Por</th>
                {isAdmin && <th className="table-header text-center" style={{ width: '90px', background: '#f8fafc' }}>Acciones</th>}
              </tr>
            </thead>
            <tbody>
              {items.map(a => {
                const checked = selectedIds.includes(a.id);
                return (
                  <tr key={a.id} className={`table-row ${checked ? 'bg-blue-50' : ''}`} data-testid={`papelera-row-${a.id}`}>
                    <td className="table-cell text-center">
                      <input type="checkbox" checked={checked} onChange={() => toggleOne(a.id)} className="w-4 h-4 rounded border-slate-300" data-testid={`papelera-check-${a.id}`} />
                    </td>
                    <td className="table-cell" style={{ whiteSpace: 'nowrap' }}>
                      <span className="text-xs font-semibold bg-slate-900 text-white px-1.5 py-0.5 rounded">{a.codigo_npl || '-'}</span>
                    </td>
                    <td className="table-cell">
                      <Link to={`/activos/${a.id}`} className="text-blue-600 hover:text-blue-700 font-medium text-sm" title={a.referencia_catastral || ''}>
                        {a.referencia_catastral || a.id_proveedor || a.id.slice(0, 8)}
                      </Link>
                    </td>
                    <td className="table-cell text-sm" style={{ maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={a.direccion || ''}>{a.direccion || '-'}</td>
                    <td className="table-cell text-sm">{a.municipio || '-'}</td>
                    <td className="table-cell text-xs text-slate-500">{a.tipo_activo_operativo || '-'}</td>
                    <td className="table-cell text-sm font-medium">{formatCurrency(a.precio_compra)}</td>
                    <td className="table-cell text-xs text-slate-500">{formatDate(a.updated_at)}</td>
                    <td className="table-cell text-xs text-slate-600" title={a.updated_by_name || ''}>{a.updated_by_name || '-'}</td>
                    {isAdmin && (
                      <td className="table-cell text-center">
                        <button
                          type="button"
                          onClick={() => handlePurgeOne(a)}
                          disabled={purgingId === a.id}
                          title="Eliminar definitivamente"
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-[8px] text-xs font-medium transition-colors disabled:opacity-50"
                          style={{
                            background: 'rgba(239, 68, 68, 0.12)',
                            color: '#FCA5A5',
                            border: '1px solid rgba(239, 68, 68, 0.35)',
                          }}
                          data-testid={`purge-btn-${a.id}`}
                        >
                          {purgingId === a.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
                          Borrar
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && total > PAGE_SIZE && (
        <div className="flex justify-end items-center gap-2 text-sm text-slate-600">
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} className="btn-secondary text-sm disabled:opacity-40" data-testid="papelera-prev">Anterior</button>
          <span>Pagina {page + 1} de {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page + 1 >= totalPages} className="btn-secondary text-sm disabled:opacity-40" data-testid="papelera-next">Siguiente</button>
        </div>
      )}
    </div>
  );
};

export default PapeleraPage;
